import { Divider, ETextStyleVariant, ETooltipSize, Text, Tooltip } from '@outdoorsyco/bonfire';
import React, { useEffect, useRef, useState } from 'react';
import { useIntl } from 'react-intl';

import { TCurrency } from '@/config/locales';
import { TEstimatedPriceBreakdown } from '@/services/types/search/rentals/id';
import { formatCurrency } from '@/utility/currency';

type TPriceDetailsPopoverProps = {
  breakdown: TEstimatedPriceBreakdown;
  currency: TCurrency;
  totalExcludesTaxes?: boolean;
  usePortal?: boolean;
  children: React.ReactNode;
};

const Row = ({ label, amount }: { label: string; amount: string }) => (
  <div className="flex justify-between gap-4">
    <Text variant={ETextStyleVariant.SmallRegular}>{label}</Text>
    <Text variant={ETextStyleVariant.SmallRegular} className="shrink-0">
      {amount}
    </Text>
  </div>
);

export const PriceDetailsPopover = ({
  breakdown,
  currency,
  totalExcludesTaxes = false,
  usePortal = true,
  children,
}: TPriceDetailsPopoverProps) => {
  const intl = useIntl();
  const [open, setOpen] = useState(false);
  // Mounting a Tooltip per card during hydration trips React's Suspense error, so defer it.
  const [hasOpened, setHasOpened] = useState(false);
  const triggerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (hasOpened) triggerRef.current?.focus({ preventScroll: true });
  }, [hasOpened]);

  const { total, original_total: originalTotal, duration: nights } = breakdown;
  const hasDiscount = total < originalTotal;
  const subtotal = hasDiscount ? originalTotal : total;
  const format = (priceInCents: number) => formatCurrency({ priceInCents, currency, digits: 2 });

  const toggle = (event: React.SyntheticEvent) => {
    event.preventDefault();
    event.stopPropagation();
    setOpen(isOpen => !isOpen);
    setHasOpened(true);
  };

  const content = (
    // Portal events still bubble to the card link, and focus must stay on the trigger.
    // eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions
    <div
      className="flex flex-col min-w-[14rem] gap-2"
      onMouseDown={event => event.preventDefault()}
      onClick={event => {
        event.preventDefault();
        event.stopPropagation();
      }}>
      <Text variant={ETextStyleVariant.SmallBold}>
        {intl.formatMessage({
          defaultMessage: 'Price details',
          id: '8ySDRC',
          description: 'Search > Listing card > Price details popup > Title',
        })}
      </Text>
      <Row
        label={intl.formatMessage(
          {
            defaultMessage: '{nightlyPrice} x {nights, plural, one {# night} other {# nights}}',
            id: 'g/VdeN',
            description: 'Search > Listing card > Price details popup > Average nightly price row',
          },
          { nightlyPrice: format(Math.round(subtotal / nights)), nights },
        )}
        amount={format(subtotal)}
      />
      {hasDiscount && (
        <Row
          label={intl.formatMessage({
            defaultMessage: 'Discount',
            id: 'iClwDv',
            description: 'Search > Listing card > Price details popup > Discount row',
          })}
          amount={`-${format(originalTotal - total)}`}
        />
      )}
      <Divider />
      <Row
        label={
          totalExcludesTaxes
            ? intl.formatMessage({
                defaultMessage: 'Total before taxes',
                description: 'Listing > Price breakdown > Total row when taxes omitted',
                id: 'Z3E9iz',
              })
            : intl.formatMessage({ defaultMessage: 'Total', id: 'MJ2jZQ' })
        }
        amount={format(total)}
      />
    </div>
  );

  const trigger = (
    <div
      ref={triggerRef}
      role="button"
      tabIndex={0}
      aria-expanded={open}
      data-testid="price-details-trigger"
      className="inline-flex underline cursor-pointer decoration-dotted underline-offset-4"
      onClick={toggle}
      onKeyDown={event => {
        if (event.key === 'Enter' || event.key === ' ') toggle(event);
        if (event.key === 'Escape' && open) {
          event.stopPropagation();
          setOpen(false);
        }
      }}
      onBlur={() => setOpen(false)}>
      {children}
    </div>
  );

  if (!hasOpened) return trigger;

  return (
    <Tooltip
      content={content}
      size={ETooltipSize.Auto}
      controlled
      open={open}
      usePortal={usePortal}>
      {trigger}
    </Tooltip>
  );
};
